import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import CreateSupplierAssignments from './CreateSupplierAssignments';
import ViewSupplierAssignments from './ViewSupplierAssignments';
import UploadSupplierAssignments from './UploadSupplierAssignments';

import { suppliersIndex } from '../../redux/actions/suppliers';
import { reset } from '../../redux/actions/assignments';

const Assignments = ({ payload, suppliers, loadSuppliers, resetAssignments }) => {
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [activeTab, setActiveTab] = useState('view');

  useEffect(() => {
    loadSuppliers(payload);
  }, []);

  const setDropdownValue = (setField, value) => {
    setField(value);
  }

  const setSupplier = (supplierId) => {
    let supplier = suppliers.suppliers.find(sup => sup.id === supplierId);
    setActiveSupplier(supplier);
  }

  const clearSupplier = () => {
    setActiveSupplier(null);
    resetAssignments();
  }

  return(
    <>
      <PageHeader stripeBackground='#fbe194' helpLink="ecwid/manage-supplier-product-assignments-2/" />
      <div className="settings-page cf">
        <div className="settings-page__header">

          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Assignments</div>
              </div>
              <div className="named-area__description">
                <p>Assign products from your store to suppliers. Start by selecting a supplier from the "Select Supplier" dropdown. If the supplier you're looking for is missing, you can manage suppliers on the <Link to="/suppliers">Suppliers page</Link>.</p>
                <p>Assigned products let you track a supplier SKU, the supplier's price for this item, and a minimum order quantity as required by the supplier.</p>
                <p>When a supplier is selected, you can view, update and remove assigned products from the "View Assignments" tab. Page through assignments in sets of 10 by clicking the "Previous Assignments" or "Next Assignments" buttons.</p>
                <p>
                  For creating new assignments, go to the "Create New Assignments" tab. 
                  Search for the products you want to assign to this supplier by typing your search into the search bar, and seeing the products found below. 
                  For products without assignments, you'll see fields to set the supplier price, minimum order quantity and the supplier's sku for the product. 
                  For products with existing assignments for this supplier, you'll see options to edit or remove the assignment.
                </p>
                <p>
                  When you have the settings input for supplier prices, skus and order quantities, make sure that the products you want to have assignments created for have been checked off, and click the "Submit New Product Assignments" button to create new assignments for this supplier.
                </p>
                <p>
                  Upload a CSV with supplier assignment information in the Upload Assignments CSV tab.
                </p>
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card-stack">


              { suppliers.isFetching &&
                <LoadingSpinner statusText="Loading Suppliers" />
              }

              { suppliers.status === 'success' && !activeSupplier && suppliers.suppliers.length > 0 &&
                <div className="a-card a-card--normal">
                  <div className="a-card__paddings">
                    <div className="fieldset fieldset--select">
                      <div className="field field--medium field--filled">
                        <label className="field__label">Suppliers</label>
                        <select onChange={(e) => setSupplier(e.target.value)} className="field__select">
                          <option hidden disabled selected={activeSupplier === null} value></option>
                          { suppliers.suppliers.map((supplier) =>
                            <option selected={activeSupplier?.id === supplier.id} value={supplier.id} key={supplier.id}>{supplier.supplier_name}</option>
                          )}
                        </select>

                        <div className="field__placeholder">Suppliers</div>
                      </div>
                    </div>
                  </div>
                </div>
              }

              { suppliers.suppliers.length === 0 && !suppliers.isFetching &&
                <div className="a-card a-card--normal">
                  <div className="a-card__paddings">
                    <div className="a-card a-card--vertical-compact a-card--info a-card--info-blue">
                      <div className="a-card__paddings text-center">
                        <p>No suppliers available. Add supplier information to see suppliers in a dropdown list here.</p>
                        <Link to="/suppliers">
                          <button
                            className="btn btn-default"
                          >
                            Manage Suppliers
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              }

              { activeSupplier && 
                <div className="a-card a-card--normal">
                  <div className="a-card__paddings">
                    
                    <h4>
                      {activeSupplier.supplier_name}
                    </h4>

                    <button className="btn btn-default" onClick={() => clearSupplier()}>
                      <span className="svg-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path fill-rule="evenodd" clip-rule="evenodd" d="M17 9A8 8 0 1 1 1 9a8 8 0 0 1 16 0Zm-4.27 4.7a6 6 0 0 1-8.796-7.916l8.795 7.917Zm1.337-1.486L5.272 4.298a6 6 0 0 1 8.795 7.916Z"/></svg>
                      </span>
                      <span className="gwt-InlineLabel">Clear supplier</span>
                    </button>

                    <hr/>


                    <div className="graphic-radio-group">
                      <div className="graphic-radio-group__items">

                        <div className="graphic-radio graphic-radio--small">
                          <label className="graphic-radio__label">
                            <input 
                              type="radio"
                              name="layout"
                              value="on"
                              className="graphic-radio__input"
                              checked={activeTab === "view"}
                              onChange={ () => setActiveTab('view') }
                            />
                            <div className="graphic-radio__icon">
                              <span className="svg-icon">
                                <svg aria-hidden="true" fill="currentColor" height="24" stroke="currentColor" stroke-width="0" viewBox="0 0 24 24" width="24" className="base__StyledIcon-sc-a9u0e1-0 emfseH"><path d="M0 0h24v24H0z" fill="none"></path><path d="M12 4C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4m0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3"></path></svg>
                              </span>
                              <div className="graphic-radio__bg"></div>
                            </div>
                            <span className="graphic-radio__title">View Assignments</span>
                          </label>
                        </div>

                        <div className="graphic-radio graphic-radio--small">
                          <label className="graphic-radio__label">
                            <input 
                              type="radio"
                              name="layout"
                              value="on"
                              className="graphic-radio__input"
                              checked={activeTab === "create"}
                              onChange={ () => setActiveTab('create') }
                            />
                            <div className="graphic-radio__icon">
                              <span className="svg-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18"><path d="M9.9968 8.0396h6.0302c.5374 0 .973.4356.973.973s-.4356.973-.973.973H9.9968V16c0 .5523-.4477 1-1 1h-.023c-.5523 0-1-.4477-1-1V9.9884H1.9744C1.4363 9.9884 1 9.5521 1 9.014s.4363-.9744.9744-.9744h5.9994V2c0-.5523.4477-1 1-1h.0244c.5523 0 1 .4477 1 1v6.0396h-.0014z"/></svg>
                              </span>
                              <div className="graphic-radio__bg"></div>
                            </div>
                            <span className="graphic-radio__title">Create New Assignments</span>
                          </label>
                        </div>

                        <div className="graphic-radio graphic-radio--small">
                          <label className="graphic-radio__label">
                            <input 
                              type="radio"
                              name="layout"
                              value="on"
                              className="graphic-radio__input"
                              checked={activeTab === "upload"}
                              onChange={ () => setActiveTab('upload') }
                            />
                            <div className="graphic-radio__icon">
                              <span className="svg-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 11a4 4 0 0 1-4 4H4.5a4.5 4.5 0 0 1-1.214-8.834 6.002 6.002 0 0 1 11.648.944A4.002 4.002 0 0 1 18 11zm-4-3a3 3 0 0 1 0 6H4.5a3.5 3.5 0 0 1-1.442-6.69.5.5 0 0 0 .99-.004 4.988 4.988 0 0 1 .051-.302 5.002 5.002 0 0 1 9.803.006A.5.5 0 0 0 14 8z" fill="currentColor"/></svg>
                              </span>
                              <div className="graphic-radio__bg"></div>
                            </div>
                            <span className="graphic-radio__title">Upload Assignments CSV</span>
                          </label>
                        </div>

                      </div>
                    </div>

                    <hr/>

                    { activeTab === 'view' &&
                      <ViewSupplierAssignments
                        payload={payload}
                        activeSupplier={activeSupplier}
                      />
                    }
                    { activeTab === 'create' &&
                      <CreateSupplierAssignments
                        payload={payload}
                        activeSupplier={activeSupplier}
                        setActiveTab={setActiveTab}
                      />
                    }
                    { activeTab === 'upload' &&
                      <UploadSupplierAssignments
                        payload={payload}
                        activeSupplier={activeSupplier}
                      />
                    }

                  </div>
                </div>
              }

            </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  suppliers: state.suppliers,
});

const mapDispatchToProps = (dispatch) => ({
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  resetAssignments: () => dispatch(reset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Assignments);